import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  Children,
  cloneElement,
  isValidElement,
} from "react";

/**
 * Reusable Carousel Component
 * Horizontal scrolling container with left/right navigation arrows
 * Supports optional auto-play functionality
 */
const Carousel = ({
  children,
  className = "",
  gap = "gap-4",
  arrowSize = "w-10 h-10 md:w-12 md:h-12",
  autoPlay = false, // Enable auto-scrolling
  autoPlayInterval = 5000, // Interval in milliseconds between auto-scrolls
  pauseOnHover = true, // Pause auto-play when hovering
  equalHeight = false, // When true, all items will have the same height as the tallest item
  resumeDelay = 3000, // Delay in ms before auto-play resumes after user interaction
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);
  const resumeTimeoutRef = useRef(null);

  // Respect the user's reduced-motion preference: when set, the carousel must
  // not auto-advance (the unprompted horizontal motion is exactly what these
  // users opt out of). Manual arrow/dot navigation stays fully functional.
  // SSR-guarded, with a listener so a live preference change is honored.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setPrefersReducedMotion(event.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // Parse gap class to get pixel value (e.g., 'gap-4' -> 16px)
  const getGapPixels = useCallback(() => {
    const gapMatch = gap.match(/gap-(\d+)/);
    if (gapMatch) {
      // Tailwind gap units: 1 unit = 0.25rem = 4px
      return parseInt(gapMatch[1], 10) * 4;
    }
    return 16; // default gap-4 = 16px
  }, [gap]);

  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;
    const epsilon = 1;

    setCanScrollLeft(scrollLeft > epsilon);
    setCanScrollRight(scrollLeft < maxScrollLeft - epsilon);
  }, []);

  const scrollRow = useCallback(
    (direction) => {
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const firstChild = container.firstElementChild;
      if (!firstChild) return;

      // Get the width of one card plus the gap
      const cardWidth = firstChild.getBoundingClientRect().width;
      const gapPixels = getGapPixels();
      const amount = (cardWidth + gapPixels) * direction;

      container.scrollBy({ left: amount, behavior: "smooth" });
    },
    [getGapPixels],
  );

  // Auto-scroll to next item, loop back to start when at end
  const autoScrollNext = useCallback(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;
    const epsilon = 1;

    if (scrollLeft >= maxScrollLeft - epsilon) {
      // At the end, scroll back to start
      container.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      // Scroll to next item
      scrollRow(1);
    }
  }, [scrollRow]);

  // Handle scroll and resize events
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScrollOrResize = () => {
      updateScrollButtons();
    };

    handleScrollOrResize();

    container.addEventListener("scroll", handleScrollOrResize);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      container.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [children, updateScrollButtons]);

  // Auto-play effect
  useEffect(() => {
    if (!autoPlay || isPaused || prefersReducedMotion) return;

    const intervalId = setInterval(() => {
      autoScrollNext();
    }, autoPlayInterval);

    return () => clearInterval(intervalId);
  }, [autoPlay, autoPlayInterval, isPaused, prefersReducedMotion, autoScrollNext]);

  // Calculate max height for equal height mode
  useEffect(() => {
    if (!equalHeight || !scrollRef.current) return;

    const calculateMaxHeight = () => {
      const container = scrollRef.current;
      if (!container) return;

      const items = container.children;
      let tallest = 0;

      // First, reset heights to auto to get natural heights
      Array.from(items).forEach((item) => {
        item.style.height = "auto";
      });

      // Measure all items and find the tallest
      Array.from(items).forEach((item) => {
        const height = item.getBoundingClientRect().height;
        if (height > tallest) {
          tallest = height;
        }
      });

      // Set max height if we found a valid value
      if (tallest > 0) {
        setMaxHeight(tallest);
      }
    };

    // Calculate after a brief delay to ensure content is rendered
    const timeoutId = setTimeout(calculateMaxHeight, 50);

    // Recalculate on resize
    window.addEventListener("resize", calculateMaxHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculateMaxHeight);
    };
  }, [equalHeight, children]);

  // Hover handlers for pause on hover
  const handleMouseEnter = () => {
    if (pauseOnHover && autoPlay) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover && autoPlay) {
      setIsPaused(false);
    }
  };

  // Touch event handling - pause auto-play during touch interaction
  useEffect(() => {
    if (!autoPlay) return;

    const container = scrollRef.current;
    if (!container) return;

    const handleTouchStart = () => {
      // Clear any pending resume timeout
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      setIsPaused(true);
    };

    const handleTouchEnd = () => {
      // Resume auto-play after a delay
      resumeTimeoutRef.current = setTimeout(() => {
        setIsPaused(false);
      }, resumeDelay);
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, [autoPlay, resumeDelay]);

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Left arrow - positioned outside the cards */}
      {canScrollLeft && (
        <div className="pointer-events-none absolute inset-y-0 -left-2 md:-left-4 flex items-center z-30">
          <button
            type="button"
            onClick={() => scrollRow(-1)}
            className="pointer-events-auto flex items-center justify-center text-white cursor-pointer transform hover:scale-125 transition-transform duration-150"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={arrowSize}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5 8.25 12l7.5-7.5"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Right arrow - positioned outside the cards */}
      {canScrollRight && (
        <div className="pointer-events-none absolute inset-y-0 -right-2 md:-right-4 flex items-center z-30">
          <button
            type="button"
            onClick={() => scrollRow(1)}
            className="pointer-events-auto flex items-center justify-center text-white cursor-pointer transform hover:scale-125 transition-transform duration-150"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={arrowSize}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Scrollable container with scroll-snap for touch snapping */}
      <div
        ref={scrollRef}
        className={`flex ${gap} overflow-x-auto scrollbar-hide pt-4 pb-4 px-1 snap-x snap-mandatory`}
      >
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            // Apply scroll-snap-align and optionally height
            const additionalStyles =
              equalHeight && maxHeight
                ? { ...child.props.style, height: `${maxHeight}px` }
                : child.props.style;

            // Add snap-start and snap-always classes to prevent skipping cards on momentum scroll
            const additionalClassName = child.props.className
              ? `${child.props.className} snap-start snap-always`
              : "snap-start snap-always";

            return cloneElement(child, {
              style: additionalStyles,
              className: additionalClassName,
            });
          }
          return child;
        })}
      </div>
    </div>
  );
};

export default Carousel;
